import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { useMemo } from 'react'
import { adminListRetentionDue } from '@/gen/cortado/v1/admin-AdminService_connectquery'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  SortableHeader,
  compareNumbers,
  compareStrings,
  useTableSort,
} from '@/components/ui/sortable-header'
import { formatDate } from '@/lib/billing'

type RetentionSortField = 'organization' | 'retentionUntil'

export const Route = createFileRoute('/_admin/billing/retention')({
  component: RetentionQueuePage,
})

const COLUMN_COUNT = 2

function RetentionQueuePage() {
  const { sortField, sortDirection, toggleSort } = useTableSort<RetentionSortField>('retentionUntil')
  const { data, isLoading, isError, error } = useQuery(adminListRetentionDue, {})

  const sortedRows = useMemo(() => {
    const rows = data?.retentionDue ?? []
    const dir = sortDirection === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      switch (sortField) {
        case 'organization':
          return compareStrings(a.organizationName, b.organizationName) * dir
        case 'retentionUntil':
          return compareNumbers(a.dataRetentionUntil?.seconds ?? 0n, b.dataRetentionUntil?.seconds ?? 0n) * dir
        default:
          return 0
      }
    })
  }, [data?.retentionDue, sortField, sortDirection])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Retention queue</h1>
        <p className="text-muted-foreground text-sm">
          Organizations past their data-retention window (R-56). This list is informational only —
          deletion stays a manual admin action; nothing here is purged automatically.
        </p>
      </div>

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Unable to load the retention queue'}
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <SortableHeader
                    active={sortField === 'organization'}
                    direction={sortDirection}
                    onToggle={() => toggleSort('organization')}
                  >
                    Organization
                  </SortableHeader>
                </TableHead>
                <TableHead>
                  <SortableHeader
                    active={sortField === 'retentionUntil'}
                    direction={sortDirection}
                    onToggle={() => toggleSort('retentionUntil')}
                  >
                    Data retained until
                  </SortableHeader>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center text-muted-foreground py-8">
                    Loading retention queue...
                  </TableCell>
                </TableRow>
              ) : !sortedRows.length ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center text-muted-foreground py-8">
                    No organizations are past their data-retention window.
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((row) => (
                  <TableRow key={row.organizationId}>
                    <TableCell>
                      <Link
                        to="/billing/$organizationId"
                        params={{ organizationId: row.organizationId }}
                        className="font-medium hover:underline"
                      >
                        {row.organizationName}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(row.dataRetentionUntil)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-muted-foreground text-xs">
        Deleting an organization's data is a manual admin action taken outside this screen, subject to
        legal, backup, and compliance review — this page only surfaces who is past the window (§5.8).
      </p>
    </div>
  )
}
